import { getUser, getToken } from "../utils/authService";
import { useState, useEffect } from "react";
import { ShoppingCart, Plus, Package, Truck, CheckCircle, Clock, XCircle, Search, Filter, Download, Eye, Trash2, FileText, Briefcase, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const PurchaseOrders = () => {
  const [activeTab, setActiveTab] = useState("purchaseOrders"); // "purchaseOrders" or "workOrders"
  
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [materialRequirements, setMaterialRequirements] = useState([]);
  const [boqItems, setBoqItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProject, setSelectedProject] = useState("");

  // PO Dialog
  const [poDialogOpen, setPoDialogOpen] = useState(false);
  const [poForm, setPoForm] = useState({
    vendor_id: "",
    project_id: "",
    delivery_address: "",
    expected_delivery: "",
    payment_terms: "Net 30",
    notes: "",
    items: [{ material_id: "", material_requirement_id: "", description: "", unit: "Nos", quantity: 0, rate: 0, amount: 0 }]
  });

  // Work Order Dialog
  const [woDialogOpen, setWoDialogOpen] = useState(false);
  const [woForm, setWoForm] = useState({
    project_id: "",
    contractor_id: "",
    description: "",
    work_category: "",
    start_date: "",
    end_date: "",
    payment_terms: "",
    special_conditions: "",
    items: [{ boq_item_id: "", description: "", unit: "", quantity: 0, rate: 0, amount: 0 }]
  });

  // View Dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);

  // Receive Dialog
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [receiveForm, setReceiveForm] = useState({
    received_date: new Date().toISOString().split('T')[0],
    received_by: "",
    invoice_number: "",
    remarks: "",
    items: []
  });

  const PAYMENT_TERMS = ["Advance", "Net 15", "Net 30", "Net 45", "Net 60", "On Delivery"];
  const UNITS = ["Nos", "Kg", "MT", "Ltr", "Cum", "Sqm", "Rm", "Bag", "Box", "Set"];
  const WORK_CATEGORIES = ["Civil Work", "Electrical Work", "Plumbing Work", "Structural Work", "Road Work", "Earthwork", "Finishing Work", "Other"];

  const currentUser = getUser() || {};
  const canManage = currentUser.role === "Master Admin" || currentUser.role === "Management";

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [poRes, vendorRes, projRes, matRes, statsRes] = await Promise.all([
        apiClient.get('/purchase-orders'),
        apiClient.get('/contractors/active'),
        apiClient.get('/projects'),
        apiClient.get('/materials/all'),
        apiClient.get('/purchase-orders/stats')
      ]);
      setPurchaseOrders(poRes.data);
      setVendors(vendorRes.data);
      setProjects(projRes.data);
      setMaterials(matRes.data);
      setStats(statsRes.data);
      
      // Fetch work orders for first project if available
      if (projRes.data.length > 0) {
        const firstProject = projRes.data[0];
        setSelectedProject(firstProject.id);
        fetchProjectData(firstProject.id);
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectData = async (projectId) => {
    try {
      const [woRes, mrRes, boqRes] = await Promise.all([
        apiClient.get(`/project-work-orders/${projectId}`),
        apiClient.get(`/material-requirements/${projectId}`),
        apiClient.get(`/boq/${projectId}`)
      ]);
      setWorkOrders(woRes.data);
      setMaterialRequirements(mrRes.data);
      setBoqItems(boqRes.data);
    } catch (error) {
      console.error("Failed to fetch project data");
    }
  };

  const handleProjectChange = (projectId) => {
    setSelectedProject(projectId);
    if (projectId) {
      fetchProjectData(projectId);
    }
  };

  const resetPoForm = () => {
    setPoForm({
      vendor_id: "",
      project_id: selectedProject || "",
      delivery_address: "",
      expected_delivery: new Date(Date.now() + 7*24*60*60*1000).toISOString().split('T')[0],
      payment_terms: "Net 30",
      notes: "",
      items: [{ material_id: "", material_requirement_id: "", description: "", unit: "Nos", quantity: 0, rate: 0, amount: 0 }]
    });
  };

  const resetWoForm = () => {
    setWoForm({
      project_id: selectedProject || "",
      contractor_id: "",
      description: "",
      work_category: "",
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
      payment_terms: "",
      special_conditions: "",
      items: [{ boq_item_id: "", description: "", unit: "", quantity: 0, rate: 0, amount: 0 }]
    });
  };

  const addPoItem = () => {
    setPoForm(prev => ({
      ...prev,
      items: [...prev.items, { 
        _tempId: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        material_id: "", 
        material_requirement_id: "", 
        description: "", 
        unit: "Nos", 
        quantity: 0, 
        rate: 0, 
        amount: 0 
      }]
    }));
  };

  const updatePoItem = (index, field, value) => {
    setPoForm(prev => {
      const items = [...prev.items];
      items[index][field] = value;
      if (field === 'quantity' || field === 'rate') {
        items[index].amount = items[index].quantity * items[index].rate;
      }
      if (field === 'material_id' && value) {
        const mat = materials.find(m => m.id === value);
        if (mat) {
          items[index].description = mat.name;
          items[index].unit = mat.unit || "Nos";
        }
      }
      // If material requirement is selected, populate material details
      if (field === 'material_requirement_id' && value) {
        const mr = materialRequirements.find(m => m.id === value);
        if (mr) {
          items[index].description = mr.material_name;
          items[index].unit = mr.unit || "Nos";
          // Calculate remaining quantity needed (planned - already received)
          const remaining = (mr.planned_quantity || 0) - (mr.received_quantity || 0);
          items[index].quantity = Math.max(0, remaining);
          // Also set the material_id if available
          if (mr.material_id) {
            items[index].material_id = mr.material_id;
          }
        }
      }
      return { ...prev, items };
    });
  };

  // Work Order Item handlers
  const addWoItem = () => {
    setWoForm(prev => ({
      ...prev,
      items: [...prev.items, { boq_item_id: "", description: "", unit: "", quantity: 0, rate: 0, amount: 0 }]
    }));
  };

  const updateWoItem = (index, field, value) => {
    setWoForm(prev => {
      const items = [...prev.items];
      items[index][field] = value;
      if (field === 'quantity' || field === 'rate') {
        items[index].amount = items[index].quantity * items[index].rate;
      }
      if (field === 'boq_item_id' && value) {
        const boq = boqItems.find(b => b.id === value);
        if (boq) {
          items[index].description = boq.description;
          items[index].unit = boq.unit || "";
          items[index].rate = boq.rate || 0;
        }
      }
      return { ...prev, items };
    });
  };

  const removeWoItem = (index) => {
    setWoForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const removePoItem = (index) => {
    setPoForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handlePoSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/purchase-orders', poForm);
      toast.success("Purchase order created");
      setPoDialogOpen(false);
      resetPoForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create PO");
    }
  };

  const handleWoSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/project-work-orders', woForm);
      toast.success("Work order created");
      setWoDialogOpen(false);
      resetWoForm();
      if (selectedProject) {
        fetchProjectData(selectedProject);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create work order");
    }
  };

  const updateWoStatus = async (woId, status) => {
    try {
      await apiClient.put(`/project-work-orders/${woId}/status?status=${status}`);
      toast.success(`Work order ${status.toLowerCase()}`);
      if (selectedProject) {
        fetchProjectData(selectedProject);
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const downloadWoPdf = async (woId) => {
    try {
      const token = getToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/project-work-orders/${woId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `work_order_${woId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Work order PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const updatePoStatus = async (poId, status) => {
    try {
      await apiClient.put(`/purchase-orders/${poId}/status?status=${status}`);
      toast.success(`PO ${status.toLowerCase()}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const openViewDialog = (po) => {
    setSelectedPO(po);
    setViewDialogOpen(true);
  };

  const openReceiveDialog = (po) => {
    setSelectedPO(po);
    setReceiveForm({
      received_date: new Date().toISOString().split('T')[0],
      received_by: currentUser.name || "",
      invoice_number: "",
      remarks: "",
      items: po.items.map(item => ({
        ...item,
        received_quantity: item.quantity,
        accepted_quantity: item.quantity,
        rejected_quantity: 0,
        rejection_reason: ""
      }))
    });
    setReceiveDialogOpen(true);
  };

  const updateReceiveItem = (index, field, value) => {
    setReceiveForm(prev => {
      const items = [...prev.items];
      items[index][field] = value;
      if (field === 'received_quantity' || field === 'rejected_quantity') {
        items[index].accepted_quantity = (parseFloat(items[index].received_quantity) || 0) - (parseFloat(items[index].rejected_quantity) || 0);
      }
      return { ...prev, items };
    });
  };

  const handleReceiveSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post(`/purchase-orders/${selectedPO.id}/receive`, receiveForm);
      toast.success("Materials received and stock updated");
      setReceiveDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to receive materials");
    }
  };

  const deletePO = async (poId) => {
    if (!window.confirm("Are you sure you want to delete this PO?")) return;
    try {
      await apiClient.delete(`/purchase-orders/${poId}`);
      toast.success("PO deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete PO");
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      "Draft": { color: "bg-slate-200 text-slate-600", icon: Clock },
      "Sent": { color: "bg-primary/20 text-primary", icon: FileText },
      "Approved": { color: "bg-accent/20 text-accent", icon: CheckCircle },
      "Issued": { color: "bg-primary/20 text-primary", icon: FileText },
      "In Progress": { color: "bg-accent/20 text-accent", icon: Clock },
      "Partially Received": { color: "bg-warning/20 text-warning", icon: Package },
      "Received": { color: "bg-success/20 text-success", icon: CheckCircle },
      "Completed": { color: "bg-success/20 text-success", icon: CheckCircle },
      "Cancelled": { color: "bg-error/20 text-error", icon: XCircle }
    };
    const { color, icon: Icon } = config[status] || config["Draft"];
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-sm text-xs font-medium ${color}`}>
        <Icon className="w-3 h-3" />
        <span>{status}</span>
      </span>
    );
  };

  const filteredPOs = purchaseOrders.filter(po => {
    const matchesFilter = filterStatus === "all" || po.status === filterStatus;
    const matchesSearch = searchTerm === "" ||
      po.po_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      po.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const poTotal = poForm.items.reduce((sum, item) => sum + (item.amount || 0), 0);
  const woTotal = woForm.items.reduce((sum, item) => sum + (item.amount || 0), 0);

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">PURCHASE & WORK ORDERS</h1>
          <p className="text-slate-600 mt-2 font-body">Manage purchase orders and work orders</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedProject}
            onChange={(e) => handleProjectChange(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          >
            <option value="">Select Project</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 mb-6 border-b border-slate-200">
        <button
          onClick={() => setActiveTab("purchaseOrders")}
          className={`px-6 py-3 font-medium text-sm ${activeTab === "purchaseOrders" 
            ? "border-b-2 border-primary text-primary" 
            : "text-slate-600 hover:text-primary"}`}
        >
          <ShoppingCart className="w-4 h-4 inline mr-2" />
          Purchase Orders
        </button>
        <button
          onClick={() => setActiveTab("workOrders")}
          className={`px-6 py-3 font-medium text-sm ${activeTab === "workOrders" 
            ? "border-b-2 border-primary text-primary" 
            : "text-slate-600 hover:text-primary"}`}
        >
          <Briefcase className="w-4 h-4 inline mr-2" />
          Work Orders
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-5 gap-4 mb-8">
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total POs</p>
          <p className="font-heading font-black text-3xl text-primary font-mono">{stats?.total_pos || 0}</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">PO Value</p>
          <p className="font-heading font-black text-2xl text-primary font-mono">₹{(stats?.total_value || 0).toLocaleString()}</p>
        </div>
        <div className="stat-card border-l-4 border-l-warning">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Pending PO</p>
          <p className="font-heading font-black text-3xl text-warning font-mono">{stats?.pending_count || 0}</p>
        </div>
        <div className="stat-card border-l-4 border-l-accent">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Work Orders</p>
          <p className="font-heading font-black text-3xl text-accent font-mono">{workOrders.length}</p>
        </div>
        <div className="stat-card border-l-4 border-l-success">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Received</p>
          <p className="font-heading font-black text-3xl text-success font-mono">{stats?.received_count || 0}</p>
        </div>
      </div>

      {/* Purchase Orders Tab */}
      {activeTab === "purchaseOrders" && (
        <>
          {/* Filters and Actions */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search PO or vendor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary w-64"
                />
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Status</option>
              <option value="Draft">Draft</option>
              <option value="Sent">Sent</option>
              <option value="Approved">Approved</option>
              <option value="Partially Received">Partially Received</option>
              <option value="Received">Received</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
        </div>
        {canManage && (
          <Dialog open={poDialogOpen} onOpenChange={setPoDialogOpen}>
            <DialogTrigger asChild>
              <button onClick={resetPoForm} className="btn-primary flex items-center space-x-2">
                <Plus className="w-4 h-4" />
                <span>Create PO</span>
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">CREATE PURCHASE ORDER</DialogTitle>
              </DialogHeader>
              <form onSubmit={handlePoSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Vendor/Supplier *</label>
                    <select
                      required
                      value={poForm.vendor_id}
                      onChange={(e) => setPoForm({...poForm, vendor_id: e.target.value})}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Select Vendor</option>
                      {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Project *</label>
                    <select
                      required
                      value={poForm.project_id}
                      onChange={(e) => {
                        const projectId = e.target.value;
                        setPoForm({...poForm, project_id: projectId, items: [{ material_id: "", material_requirement_id: "", description: "", unit: "Nos", quantity: 0, rate: 0, amount: 0 }]});
                        if (projectId) {
                          fetchProjectData(projectId);
                        }
                      }}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Select Project</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                </div>

                {/* Project Material Requirements Info */}
                {poForm.project_id && materialRequirements.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
                    <p className="text-xs text-blue-700 font-medium">
                      <LinkIcon className="w-3 h-3 inline mr-1" />
                      {materialRequirements.length} Material Requirements found for this project. Link your PO items to track procurement against requirements.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Expected Delivery *</label>
                    <input
                      type="date"
                      required
                      value={poForm.expected_delivery}
                      onChange={(e) => setPoForm({...poForm, expected_delivery: e.target.value})}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Payment Terms</label>
                    <select
                      value={poForm.payment_terms}
                      onChange={(e) => setPoForm({...poForm, payment_terms: e.target.value})}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {PAYMENT_TERMS.map(term => <option key={term} value={term}>{term}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Delivery Address</label>
                  <input
                    type="text"
                    value={poForm.delivery_address}
                    onChange={(e) => setPoForm({...poForm, delivery_address: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Site/project delivery address"
                  />
                </div>

                {/* Line Items */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-xs font-medium text-slate-700 uppercase tracking-widest">Order Items</label>
                    <button type="button" onClick={addPoItem} className="text-primary text-sm hover:underline">+ Add Item</button>
                  </div>
                  <div className="border border-slate-200 rounded-sm overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="text-left py-2 px-3 text-xs uppercase">Material</th>
                          <th className="text-left py-2 px-3 text-xs uppercase w-36">Link to Requirement</th>
                          <th className="text-left py-2 px-3 text-xs uppercase w-32">Description</th>
                          <th className="text-center py-2 px-3 text-xs uppercase w-16">Unit</th>
                          <th className="text-center py-2 px-3 text-xs uppercase w-16">Qty</th>
                          <th className="text-center py-2 px-3 text-xs uppercase w-20">Rate</th>
                          <th className="text-right py-2 px-3 text-xs uppercase w-24">Amount</th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {poForm.items.map((item, idx) => (
                          <tr key={item._tempId || `po-item-${idx}`} className="border-t border-slate-200">
                            <td className="py-2 px-2">
                              <select
                                value={item.material_id}
                                onChange={(e) => updatePoItem(idx, 'material_id', e.target.value)}
                                className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm"
                              >
                                <option value="">Select</option>
                                {materials.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                              </select>
                            </td>
                            <td className="py-2 px-2">
                              <select
                                value={item.material_requirement_id}
                                onChange={(e) => updatePoItem(idx, 'material_requirement_id', e.target.value)}
                                className={`w-full px-2 py-1 border rounded-sm text-sm ${
                                  !poForm.project_id ? 'border-orange-300 bg-orange-50' : 'border-slate-300'
                                }`}
                                data-testid={`po-item-${idx}-material-req`}
                                disabled={!poForm.project_id}
                              >
                                {!poForm.project_id ? (
                                  <option value="">Select Project First</option>
                                ) : materialRequirements.length === 0 ? (
                                  <option value="">No Requirements</option>
                                ) : (
                                  <>
                                    <option value="">-- Select Requirement --</option>
                                    {materialRequirements
                                      .filter(mr => !item.material_id || mr.material_id === item.material_id)
                                      .map(mr => (
                                        <option key={mr.id} value={mr.id}>
                                          {mr.material_name} - Need: {mr.planned_quantity} {mr.unit}
                                        </option>
                                      ))
                                    }
                                  </>
                                )}
                              </select>
                            </td>
                            <td className="py-2 px-2">
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) => updatePoItem(idx, 'description', e.target.value)}
                                className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm"
                                placeholder="Description"
                              />
                            </td>
                            <td className="py-2 px-2">
                              <select
                                value={item.unit}
                                onChange={(e) => updatePoItem(idx, 'unit', e.target.value)}
                                className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm"
                              >
                                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                              </select>
                            </td>
                            <td className="py-2 px-2">
                              <input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updatePoItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm font-mono text-center"
                              />
                            </td>
                            <td className="py-2 px-2">
                              <input
                                type="number"
                                value={item.rate}
                                onChange={(e) => updatePoItem(idx, 'rate', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm font-mono text-center"
                              />
                            </td>
                            <td className="py-2 px-2 text-right font-mono text-sm font-semibold">
                              ₹{item.amount.toLocaleString()}
                            </td>
                            <td className="py-2 px-2">
                              {poForm.items.length > 1 && (
                                <button type="button" onClick={() => removePoItem(idx)} className="text-error hover:bg-red-100 p-1 rounded">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-100">
                        <tr>
                          <td colSpan="5" className="py-2 px-3 text-right font-semibold">Total:</td>
                          <td className="py-2 px-3 text-right font-mono font-bold text-primary">₹{poTotal.toLocaleString()}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Notes</label>
                  <textarea
                    value={poForm.notes}
                    onChange={(e) => setPoForm({...poForm, notes: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    rows={2}
                    placeholder="Any additional notes or instructions..."
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button type="button" onClick={() => setPoDialogOpen(false)} className="btn-outline">Cancel</button>
                  <button type="submit" className="btn-primary">Create PO</button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* PO Table */}
      <div className="stat-card overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-slate-300">
              <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">PO Number</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Vendor</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Project</th>
              <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Date</th>
              <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Delivery</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Amount</th>
              <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Status</th>
              <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPOs.map(po => (
              <tr key={po.id} className="border-b border-slate-200 hover:bg-slate-50">
                <td className="py-3 px-4 font-mono text-sm font-medium text-primary">{po.po_number}</td>
                <td className="py-3 px-4 text-sm">{po.vendor_name}</td>
                <td className="py-3 px-4 text-sm">{po.project_name}</td>
                <td className="py-3 px-4 text-center font-mono text-sm">{po.order_date}</td>
                <td className="py-3 px-4 text-center font-mono text-sm">{po.expected_delivery}</td>
                <td className="py-3 px-4 text-right font-mono text-sm font-semibold">₹{po.total_amount?.toLocaleString()}</td>
                <td className="py-3 px-4 text-center">{getStatusBadge(po.status)}</td>
                <td className="py-3 px-4 text-center">
                  <div className="flex items-center justify-center space-x-2">
                    <button onClick={() => openViewDialog(po)} className="p-1 hover:bg-slate-200 rounded" title="View">
                      <Eye className="w-4 h-4 text-slate-600" />
                    </button>
                    {po.status === "Draft" && canManage && (
                      <>
                        <button onClick={() => updatePoStatus(po.id, "Sent")} className="text-xs text-primary hover:underline">Send</button>
                        <button onClick={() => deletePO(po.id)} className="text-xs text-error hover:underline">Delete</button>
                      </>
                    )}
                    {po.status === "Sent" && canManage && (
                      <button onClick={() => updatePoStatus(po.id, "Approved")} className="text-xs text-accent hover:underline">Approve</button>
                    )}
                    {(po.status === "Approved" || po.status === "Partially Received") && (
                      <button onClick={() => openReceiveDialog(po)} className="text-xs text-success hover:underline">Receive</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredPOs.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-slate-400" />
            <h3 className="font-heading font-bold text-xl mb-2">No Purchase Orders</h3>
            <p className="text-slate-600 mb-4">Create purchase orders to procure materials from vendors.</p>
          </div>
        )}
      </div>

      {/* View PO Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">PURCHASE ORDER: {selectedPO?.po_number}</DialogTitle>
          </DialogHeader>
          {selectedPO && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><strong>Vendor:</strong> {selectedPO.vendor_name}</div>
                <div><strong>Project:</strong> {selectedPO.project_name}</div>
                <div><strong>Order Date:</strong> {selectedPO.order_date}</div>
                <div><strong>Expected Delivery:</strong> {selectedPO.expected_delivery}</div>
                <div><strong>Payment Terms:</strong> {selectedPO.payment_terms}</div>
                <div><strong>Status:</strong> {getStatusBadge(selectedPO.status)}</div>
              </div>
              {selectedPO.delivery_address && (
                <div className="text-sm"><strong>Delivery Address:</strong> {selectedPO.delivery_address}</div>
              )}
              <div className="border border-slate-200 rounded-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left py-2 px-3 text-xs uppercase">Item</th>
                      <th className="text-center py-2 px-3 text-xs uppercase">Unit</th>
                      <th className="text-center py-2 px-3 text-xs uppercase">Qty</th>
                      <th className="text-right py-2 px-3 text-xs uppercase">Rate</th>
                      <th className="text-right py-2 px-3 text-xs uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPO.items?.map((item, idx) => (
                      <tr key={item.material_id || item._tempId || `display-item-${idx}`} className="border-t border-slate-200">
                        <td className="py-2 px-3 text-sm">{item.description}</td>
                        <td className="py-2 px-3 text-center text-sm">{item.unit}</td>
                        <td className="py-2 px-3 text-center font-mono text-sm">{item.quantity}</td>
                        <td className="py-2 px-3 text-right font-mono text-sm">₹{item.rate?.toLocaleString()}</td>
                        <td className="py-2 px-3 text-right font-mono text-sm font-semibold">₹{item.amount?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-100">
                    <tr>
                      <td colSpan="4" className="py-2 px-3 text-right font-semibold">Total:</td>
                      <td className="py-2 px-3 text-right font-mono font-bold text-primary">₹{selectedPO.total_amount?.toLocaleString()}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {selectedPO.notes && (
                <div className="text-sm"><strong>Notes:</strong> {selectedPO.notes}</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Receive Materials Dialog */}
      <Dialog open={receiveDialogOpen} onOpenChange={setReceiveDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">RECEIVE MATERIALS</DialogTitle>
          </DialogHeader>
          {selectedPO && (
            <form onSubmit={handleReceiveSubmit} className="space-y-4 mt-4">
              <div className="bg-slate-50 p-3 rounded-sm">
                <p className="text-sm"><strong>PO:</strong> {selectedPO.po_number} | <strong>Vendor:</strong> {selectedPO.vendor_name}</p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Received Date *</label>
                  <input
                    type="date"
                    required
                    value={receiveForm.received_date}
                    onChange={(e) => setReceiveForm({...receiveForm, received_date: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Received By</label>
                  <input
                    type="text"
                    value={receiveForm.received_by}
                    onChange={(e) => setReceiveForm({...receiveForm, received_by: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Invoice No.</label>
                  <input
                    type="text"
                    value={receiveForm.invoice_number}
                    onChange={(e) => setReceiveForm({...receiveForm, invoice_number: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono"
                  />
                </div>
              </div>

              <div className="border border-slate-200 rounded-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left py-2 px-3 text-xs uppercase">Item</th>
                      <th className="text-center py-2 px-3 text-xs uppercase">Ordered</th>
                      <th className="text-center py-2 px-3 text-xs uppercase">Received</th>
                      <th className="text-center py-2 px-3 text-xs uppercase">Rejected</th>
                      <th className="text-center py-2 px-3 text-xs uppercase">Accepted</th>
                      <th className="text-left py-2 px-3 text-xs uppercase">Rejection Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receiveForm.items.map((item, idx) => (
                      <tr key={item.material_id || `receive-item-${idx}`} className="border-t border-slate-200">
                        <td className="py-2 px-3 text-sm">{item.description}</td>
                        <td className="py-2 px-3 text-center font-mono text-sm">{item.quantity} {item.unit}</td>
                        <td className="py-2 px-2">
                          <input
                            type="number"
                            value={item.received_quantity}
                            onChange={(e) => updateReceiveItem(idx, 'received_quantity', e.target.value)}
                            className="w-20 px-2 py-1 border border-slate-300 rounded-sm text-sm font-mono text-center"
                          />
                        </td>
                        <td className="py-2 px-2">
                          <input
                            type="number"
                            value={item.rejected_quantity}
                            onChange={(e) => updateReceiveItem(idx, 'rejected_quantity', e.target.value)}
                            className="w-20 px-2 py-1 border border-slate-300 rounded-sm text-sm font-mono text-center"
                          />
                        </td>
                        <td className="py-2 px-3 text-center font-mono text-sm font-semibold text-success">{item.accepted_quantity}</td>
                        <td className="py-2 px-2">
                          <input
                            type="text"
                            value={item.rejection_reason}
                            onChange={(e) => updateReceiveItem(idx, 'rejection_reason', e.target.value)}
                            className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm"
                            placeholder="If rejected..."
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Remarks</label>
                <textarea
                  value={receiveForm.remarks}
                  onChange={(e) => setReceiveForm({...receiveForm, remarks: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm"
                  rows={2}
                  placeholder="Any remarks about the delivery..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setReceiveDialogOpen(false)} className="btn-outline">Cancel</button>
                <button type="submit" className="btn-primary">Receive Materials</button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
        </>
      )}

      {/* Work Orders Tab */}
      {activeTab === "workOrders" && (
        <>
          <div className="flex justify-between items-center mb-6">
            <div className="text-sm text-slate-600">
              {selectedProject ? `Showing work orders for selected project` : "Select a project to view work orders"}
            </div>
            <button onClick={() => { resetWoForm(); setWoDialogOpen(true); }} className="btn-primary flex items-center space-x-2">
              <Plus className="w-4 h-4" />
              <span>Create Work Order</span>
            </button>
          </div>

          <div className="stat-card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">WO Number</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Contractor</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Work Category</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Start Date</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">End Date</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Amount</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Status</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {workOrders.map((wo) => (
                  <tr key={wo.id} className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="py-3 px-4 font-mono text-sm font-medium text-primary">{wo.work_order_number}</td>
                    <td className="py-3 px-4 text-sm">{wo.contractor_name}</td>
                    <td className="py-3 px-4 text-sm">{wo.work_category}</td>
                    <td className="py-3 px-4 text-center font-mono text-sm">{wo.start_date}</td>
                    <td className="py-3 px-4 text-center font-mono text-sm">{wo.end_date}</td>
                    <td className="py-3 px-4 text-right font-mono text-sm font-semibold">₹{wo.total_amount?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-center">{getStatusBadge(wo.status)}</td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button onClick={() => downloadWoPdf(wo.id)} className="p-1 hover:bg-slate-200 rounded" title="Download PDF">
                          <Download className="w-4 h-4 text-slate-600" />
                        </button>
                        {wo.status === "Draft" && canManage && (
                          <button onClick={() => updateWoStatus(wo.id, "Issued")} className="text-xs text-primary hover:underline">Issue</button>
                        )}
                        {wo.status === "Issued" && canManage && (
                          <button onClick={() => updateWoStatus(wo.id, "In Progress")} className="text-xs text-accent hover:underline">Start</button>
                        )}
                        {wo.status === "In Progress" && canManage && (
                          <button onClick={() => updateWoStatus(wo.id, "Completed")} className="text-xs text-success hover:underline">Complete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {workOrders.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Briefcase className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                <h3 className="font-heading font-bold text-xl mb-2">No Work Orders</h3>
                <p className="text-slate-600 mb-4">Create work orders to assign work to contractors.</p>
              </div>
            )}
          </div>

          {/* Create Work Order Dialog */}
          <Dialog open={woDialogOpen} onOpenChange={setWoDialogOpen}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">CREATE WORK ORDER</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleWoSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Project *</label>
                    <select
                      required
                      value={woForm.project_id}
                      onChange={(e) => {
                        setWoForm({...woForm, project_id: e.target.value});
                        if (e.target.value) fetchProjectData(e.target.value);
                      }}
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm"
                    >
                      <option value="">Select Project</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Contractor *</label>
                    <select
                      required
                      value={woForm.contractor_id}
                      onChange={(e) => setWoForm({...woForm, contractor_id: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm"
                    >
                      <option value="">Select Contractor</option>
                      {vendors.map(v => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Work Category *</label>
                    <select
                      required
                      value={woForm.work_category}
                      onChange={(e) => setWoForm({...woForm, work_category: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm"
                    >
                      <option value="">Select Category</option>
                      {WORK_CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Description *</label>
                    <input
                      type="text"
                      required
                      value={woForm.description}
                      onChange={(e) => setWoForm({...woForm, description: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm"
                      placeholder="Work order description"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Start Date *</label>
                    <input
                      type="date"
                      required
                      value={woForm.start_date}
                      onChange={(e) => setWoForm({...woForm, start_date: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">End Date *</label>
                    <input
                      type="date"
                      required
                      value={woForm.end_date}
                      onChange={(e) => setWoForm({...woForm, end_date: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm font-mono"
                    />
                  </div>
                </div>

                {/* Work Order Items linked with BOQ */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-xs font-medium text-slate-700 uppercase tracking-widest">Work Items (Link with BOQ)</label>
                    <button type="button" onClick={addWoItem} className="text-sm text-primary hover:underline">+ Add Item</button>
                  </div>
                  <div className="border border-slate-200 rounded-sm overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="text-left py-2 px-3 text-xs uppercase">BOQ Item</th>
                          <th className="text-left py-2 px-3 text-xs uppercase">Description</th>
                          <th className="text-center py-2 px-3 text-xs uppercase">Unit</th>
                          <th className="text-center py-2 px-3 text-xs uppercase">Qty</th>
                          <th className="text-right py-2 px-3 text-xs uppercase">Rate</th>
                          <th className="text-right py-2 px-3 text-xs uppercase">Amount</th>
                          <th className="w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {woForm.items.map((item, idx) => (
                          <tr key={item.boq_item_id || item._tempId || `wo-item-${idx}`} className="border-t border-slate-200">
                            <td className="py-2 px-2">
                              <select
                                value={item.boq_item_id}
                                onChange={(e) => updateWoItem(idx, 'boq_item_id', e.target.value)}
                                className="w-full px-2 py-1 border rounded text-sm"
                              >
                                <option value="">Select BOQ</option>
                                {boqItems.map(b => (
                                  <option key={b.id} value={b.id}>{b.item_code} - {b.description?.slice(0, 30)}</option>
                                ))}
                              </select>
                            </td>
                            <td className="py-2 px-2">
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) => updateWoItem(idx, 'description', e.target.value)}
                                className="w-full px-2 py-1 border rounded text-sm"
                              />
                            </td>
                            <td className="py-2 px-2">
                              <input
                                type="text"
                                value={item.unit}
                                onChange={(e) => updateWoItem(idx, 'unit', e.target.value)}
                                className="w-16 px-2 py-1 border rounded text-center text-sm"
                              />
                            </td>
                            <td className="py-2 px-2">
                              <input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateWoItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                                className="w-20 px-2 py-1 border rounded text-center font-mono text-sm"
                              />
                            </td>
                            <td className="py-2 px-2">
                              <input
                                type="number"
                                value={item.rate}
                                onChange={(e) => updateWoItem(idx, 'rate', parseFloat(e.target.value) || 0)}
                                className="w-24 px-2 py-1 border rounded text-right font-mono text-sm"
                              />
                            </td>
                            <td className="py-2 px-2 text-right font-mono text-sm font-semibold">₹{item.amount?.toLocaleString()}</td>
                            <td className="py-2 px-1">
                              {woForm.items.length > 1 && (
                                <button type="button" onClick={() => removeWoItem(idx)} className="text-error hover:bg-red-100 p-1 rounded">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-100">
                        <tr>
                          <td colSpan="5" className="py-2 px-3 text-right font-semibold">Total:</td>
                          <td className="py-2 px-3 text-right font-mono font-bold text-primary">₹{woTotal.toLocaleString()}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Payment Terms</label>
                    <input
                      type="text"
                      value={woForm.payment_terms}
                      onChange={(e) => setWoForm({...woForm, payment_terms: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm"
                      placeholder="e.g., 30% advance, 70% on completion"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Special Conditions</label>
                    <input
                      type="text"
                      value={woForm.special_conditions}
                      onChange={(e) => setWoForm({...woForm, special_conditions: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm"
                      placeholder="Any special terms..."
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button type="button" onClick={() => setWoDialogOpen(false)} className="btn-outline">Cancel</button>
                  <button type="submit" className="btn-primary">Create Work Order</button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default PurchaseOrders;
